import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/apiHelper';

import { formatPrice } from '../../utils/formatHelper';
import ErrorBoundary from '../../components/ErrorBoundary';
import { useAuth } from '../../context/AuthContext';
import { showServerError, showError } from '../../utils/toastHelper';

function CompanyStats() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    console.log('User:', user);
    console.log('Company ID:', id);
    // COMPANY_ADMIN rolü sadece kendi şirketine erişebilir
    if (user?.role === 'COMPANY_ADMIN' && user?.companyId && user.companyId.toString() !== id) {
      console.log('Access denied for user to company:', id);
      console.log('User company ID:', user.companyId);
      console.log('User role:', user.role);
      setAccessDenied(true);
      setError('Bu şirketin istatistiklerine erişim yetkiniz bulunmamaktadır');
      setLoading(false);
      return;
    }

    const fetchCompanyStats = async () => {
      try {
        setLoading(true);
        setError(null);

        // Önce şirket bilgilerini al
        const companyResponse = await apiRequest(`/companies/${id}`);
        if (!companyResponse.success) {
          throw new Error(companyResponse.message || 'Şirket bilgileri alınamadı');
        }
        setCompany(companyResponse.company);

        // Sonra şirket istatistiklerini al
        const statsResponse = await apiRequest(`/companies/${id}/stats`);
        if (!statsResponse.success) {
          throw new Error(statsResponse.message || 'Şirket istatistikleri alınamadı');
        }
        setStats(statsResponse.stats);
      } catch (error) {
        console.error('Failed to fetch company stats:', error);
        // 401 hatası için ek toast gösterme (zaten showSessionTimeout çağrıldı)
        if (error.isAuthError) {
          setError('Oturum süresi doldu. Lütfen tekrar giriş yapın.');
          return;
        }
        if (error.apiError) {
          showServerError();
        } else {
          showError(error.message || 'Şirket istatistikleri yüklenirken bir hata oluştu');
        }
        setError(error.message || 'Şirket istatistikleri yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    if (!accessDenied) {
      fetchCompanyStats();
    }

    // Responsive davranış için pencere boyutu değişikliklerini izle
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);

  }, [id, user, accessDenied]);

  if (loading) {
    return (
      <div className="container-fluid p-0">
        <div className="text-center py-5">
          <div className="spinner-border text-orange" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="container-fluid p-0">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Bu şirketin istatistiklerine erişim yetkiniz bulunmamaktadır
        </div>
        <div className="mt-3">
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate(user?.role === 'COMPANY_ADMIN' ? '/admin' : '/admin/companies')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            {user?.role === 'COMPANY_ADMIN' ? 'Dashboard\'a Geri Dön' : 'Şirketlere Geri Dön'}
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid p-0">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
        <div className="mt-3">
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate(user?.role === 'COMPANY_ADMIN' ? '/admin' : '/admin/companies')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            {user?.role === 'COMPANY_ADMIN' ? 'Dashboard\'a Geri Dön' : 'Şirketlere Geri Dön'}
          </button>
        </div>
      </div>
    );
  }

  if (!stats || !company) {
    return (
      <div className="container-fluid p-0">
        <div className="alert alert-warning">
          <i className="fas fa-info-circle me-2"></i>
          İstatistik bilgisi bulunamadı
        </div>
        <div className="mt-3">
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate(user?.role === 'COMPANY_ADMIN' ? '/admin' : '/admin/companies')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            {user?.role === 'COMPANY_ADMIN' ? 'Dashboard\'a Geri Dön' : 'Şirketlere Geri Dön'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="container-fluid p-0">
        {/* Üst Başlık ve Butonlar */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
          <div className="mb-3 mb-md-0">
            <h4 className="mb-1">{company.name} - İstatistikler</h4>
            <p className="text-muted mb-0">Şirket abonelik ve kullanıcı istatistikleri</p>
          </div>
          <button
            className="btn btn-outline-secondary w-100 w-md-auto btn-sm-web"
            onClick={() => navigate(user?.role === 'COMPANY_ADMIN' ? '/admin' : '/admin/companies')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            {user?.role === 'COMPANY_ADMIN' ? 'Dashboard\'a Geri Dön' : 'Şirketlere Geri Dön'}
          </button>
        </div>

        {/* İstatistik Kartları */}
        <div className="row g-4">
          {/* Abonelik İstatistikleri */}
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="mb-0">
                  <i className="fas fa-sync me-2 text-orange"></i>
                  Abonelik İstatistikleri
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3">
                      <div className="small text-muted mb-1">Toplam Abonelik</div>
                      <div className="fs-5 fs-md-4 fw-bold">{stats.subscriptions.total}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3">
                      <div className="small text-muted mb-1">Aktif Abonelik</div>
                      <div className="fs-5 fs-md-4 fw-bold text-success">{stats.subscriptions.active}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3">
                      <div className="small text-muted mb-1">İptal Edilen</div>
                      <div className="fs-5 fs-md-4 fw-bold text-danger">{stats.subscriptions.cancelled}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3">
                      <div className="small text-muted mb-1">Ücretsiz Abonelik</div>
                      <div className="fs-5 fs-md-4 fw-bold">{stats.subscriptions.free}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3">
                      <div className="small text-muted mb-1">Ücretli Abonelik</div>
                      <div className="fs-5 fs-md-4 fw-bold">{stats.subscriptions.paid}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3 bg-light">
                      <div className="small text-muted mb-1">Toplam Gelir</div>
                      <div className="fs-5 fs-md-4 fw-bold text-primary">{stats.subscriptions.totalRevenue ? formatPrice(stats.subscriptions.totalRevenue) : '0 TL'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Kullanıcı İstatistikleri */}
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="mb-0">
                  <i className="fas fa-users me-2 text-orange"></i>
                  Kullanıcı İstatistikleri
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3">
                      <div className="small text-muted mb-1">Toplam Kullanıcı</div>
                      <div className="fs-5 fs-md-4 fw-bold">{stats.users.total}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3">
                      <div className="small text-muted mb-1">Aktif Kullanıcı</div>
                      <div className="fs-5 fs-md-4 fw-bold text-success">{stats.users.active}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3">
                      <div className="small text-muted mb-1">Müşteri</div>
                      <div className="fs-5 fs-md-4 fw-bold">{stats.users.customers}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3">
                      <div className="small text-muted mb-1">Kargo Personeli</div>
                      <div className="fs-5 fs-md-4 fw-bold">{stats.users.shippingStaff}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3">
                      <div className="small text-muted mb-1">Yöneticiler</div>
                      <div className="fs-5 fs-md-4 fw-bold">{stats.users.admins}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-6">
                    <div className="border rounded p-3 bg-light">
                      <div className="small text-muted mb-1">Aktif Oranı</div>
                      <div className="fs-5 fs-md-4 fw-bold">
                        %{Math.round((stats.users.active / (stats.users.total || 1)) * 100) || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Şirket Bilgileri */}
        <div className="card shadow-sm mt-4">
          <div className="card-header bg-white">
            <h5 className="mb-0">
              <i className="fas fa-building me-2 text-orange"></i>
              Şirket Bilgileri
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-6 col-md-6">
                <div className="border rounded p-3">
                  <div className="small text-muted mb-1">Şirket ID</div>
                  <div className="text-truncate">{company.id}</div>
                </div>
              </div>
              <div className="col-6 col-md-6">
                <div className="border rounded p-3">
                  <div className="small text-muted mb-1">Durum</div>
                  <div>
                    <span className={`badge bg-${company.status === 'ACTIVE' ? 'success' : 'secondary'}`}>
                      {company.status === 'ACTIVE' ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-6">
                <div className="border rounded p-3">
                  <div className="small text-muted mb-1">Oluşturma Tarihi</div>
                  <div>{new Date(company.createdAt).toLocaleDateString('tr-TR')}</div>
                </div>
              </div>
              <div className="col-6 col-md-6">
                <div className="border rounded p-3">
                  <div className="small text-muted mb-1">Son Güncelleme</div>
                  <div>{new Date(company.updatedAt).toLocaleDateString('tr-TR')}</div>
                </div>
              </div>
              {company.description && (
                <div className="col-12">
                  <div className="border rounded p-3">
                    <div className="small text-muted mb-1">Açıklama</div>
                    <div>{company.description}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 767px) {
            .fs-5 {
              font-size: 1rem !important;
            }
            
            .border.rounded.p-3 {
              padding: 0.75rem !important;
            }
            
            .card-body {
              padding: 1rem;
            }
            
            .small {
              font-size: 0.7rem !important;
            }
          }
          
          @media (min-width: 768px) {
            .btn-sm-web {
              padding: 0.375rem 0.75rem;
              font-size: 0.875rem;
              width: auto !important;
              max-width: 200px;
            }
          }
        `}</style>
      </div>
    </ErrorBoundary>
  );
}

export default CompanyStats; 